import { Directive, inject, OnInit } from '@angular/core';
import { RelatedProductsService } from '@df/catalog-ui/related-products/related-products.service';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import { ICmsContent } from '@icms/core/content';
import { Product } from '../../../product/product.decorator';
import { ModelCarouselComponentDef } from '../model-carousel/model-carousel.component.def';
import { IModelCarouselData, IModelCarouselLoadRequest } from '../model-carousel/model-carousel.interface';

export interface IAbstractProductCarouselData extends IModelCarouselData, KlevuSearch.IRecommendationsModelCarouselLoadRequest {
    showCarouselOnFacets?: string;
    attribute?: string;
    klevuRecommendationId?: string;
    strategyId: string;
    isRecentlyViewed?: boolean;
    variant: any;
}

@Directive()
export abstract class AbstractProductsCarouselComponent<T extends IAbstractProductCarouselData>
    extends ModelCarouselComponentDef<Product, T>
    implements OnInit
{
    @ICmsContent({
        type: 'component'
    })
    override component?: T;

    protected override canDetach = false;

    type = 'product';
    private klevuTrackService = inject(KlevuTrackService);

    relatedPrroductsService = inject(RelatedProductsService);

    app = inject(App);

    private abIfService = inject(AbIfService);

    experimentModelsLength = 12;
    disabled = false;

    displayedModels?: Product[];

    override ngOnInit(): void {
        super.ngOnInit();
        if (this.app.meta.model?.type && ['product', 'category'].indexOf(this.app.meta.model.type) !== -1) {
            this.subscriptions.add(
                this.app.meta.subscribe(() => {
                    const promise = this.app.meta.model?.load(2);
                    promise && promise.then(() => this.setModels());
                    this.detectChanges();
                })
            );
            this.subscriptions.add(
                this.abIfService.getState('TARGETING').subscribe(() => {
                    this.onModelsSet();
                })
            );
        }
    }

    loadMoreModels() {
        if (this.disabled) {
            return;
        }
        const filteredModels = this.filteredModels;
        const experimentModelsLength = this.experimentModelsLength;
        if (experimentModelsLength + 6 > filteredModels.length) {
            this.experimentModelsLength = experimentModelsLength + (filteredModels.length - this.experimentModelsLength);
            this.disabled = true;
        } else {
            this.experimentModelsLength = experimentModelsLength + 6;
        }

        this.detectChanges();
    }

    get filteredModels() {
        return (this.listing?.models as Product<IProductDataV2>[])?.filter(model => !!model?.data?.url);
    }

    get showExperimentalVersion() {
        return (
            this.component?.variant === 'klevuContextRecommendations' &&
            (this.client.isS || this.client.isM) &&
            this.app.ab.getVariant('TARGETING') === 'B'
        );
    }

    get isKlevuRecomendations(): boolean {
        const variant = this.component?.variant as string;
        return [KlevuSearch.contextRecommendationsEngineId].indexOf(variant) !== -1;
    }

    get klevuTypeId(): string | undefined {
        if (this.isKlevuRecomendations) {
            return this.component?.klevuTypeId;
        }
    }

    get klevuRecommendationId(): string | undefined {
        if (this.isKlevuRecomendations) {
            return this.component?.klevuRecommendationId;
        }
    }

    get isFacet(): boolean {
        return !!this.app.meta.state?.route?.data?.facet?.category;
    }

    protected override onModelsSet(): void {
        if (this.isKlevuRecomendations && this.component) {
            const products = this.listing?.getSourceModels() as Product<IProductDataV2>[];
            if (products?.length) {
                const klevuRecommendationId = this.klevuRecommendationId;
                if (klevuRecommendationId) {
                    this.klevuTrackService.trackRecommendation({
                        eventListId: klevuRecommendationId,
                        event: KlevuSearch.EAnalyticsRecommendationEvent.view,
                        products: products.map((product, index) => {
                            return {
                                model: product,
                                position: index
                            };
                        })
                    });
                }
            }
        }
        const filteredModels = this.filteredModels;
        if (this.showExperimentalVersion) {
            this.displayedModels = filteredModels.slice(0, this.experimentModelsLength) || [];
        } else {
            this.displayedModels = filteredModels || [];
        }
        this.detectChanges();
    }
    get listData(): string {
        return this.component?.title + ':' + this.app.meta.state?.url;
    }
    protected override getLoadRequest(): IModelCarouselLoadRequest | undefined {
        const loadRequest = { ...super.getLoadRequest() } as IModelCarouselLoadRequest;
        if ((this.component?.variant as string) === 'attribute') {
            const model = this.app.meta.model;
            if (model?.type === 'product') {
                loadRequest.ids = [model?.id as number];
                loadRequest['attributeCode'] = ['nyw_item_range_code_nh', this.component?.attribute].filter(i => !!i);
            }
        }
        return loadRequest;
    }
}

export function getAttributeOptions() {
    return (
        (<App>window['app'])?.resources?.attribute?.getAllExisting().map(i => {
            return {
                key: i.id,
                value: i.id,
                title: i.data?.label
            };
        }) || []
    );
}